import { useQueryClient, useMutation } from "@tanstack/react-query";
import { MapPin } from "lucide-react";
import { grantBranchAccessRequest, denyBranchAccessRequest } from "../api/api";
import { usePendingBranchAccessRequests } from "../hooks/queries";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { UserAvatar } from "../components/ui/user-avatar";
import { EmptyState } from "../components/ui/empty-state";
import { toast } from "../lib/toast";

// Only pending requests are exposed by the API (GET /branch-requests), so
// there's no "afgehandeld" tab — a handled request simply drops off the list.
export default function Toegangsverzoeken() {
  const qc = useQueryClient();
  const { data, isLoading } = usePendingBranchAccessRequests();
  const requests = data ?? [];

  function refresh() {
    qc.invalidateQueries({ queryKey: ["branch-requests"] });
    qc.invalidateQueries({ queryKey: ["tasks"] });
  }

  const grantMut = useMutation({
    mutationFn: (id: number) => grantBranchAccessRequest(id),
    onSuccess: () => toast("Toegang gegeven", { kind: "success" }),
    onSettled: refresh,
  });

  const denyMut = useMutation({
    mutationFn: (id: number) => denyBranchAccessRequest(id),
    onSuccess: () => toast("Verzoek afgewezen", { kind: "success" }),
    onSettled: refresh,
  });

  const busy = grantMut.isPending || denyMut.isPending;

  return (
    <Layout
      title="Toegangsverzoeken"
      subtitle={isLoading ? "Laden…" : requests.length > 0 ? `${requests.length} open ${requests.length === 1 ? "verzoek" : "verzoeken"}` : "Geen open verzoeken"}
    >
      <div className="mx-auto flex max-w-[1000px] flex-col gap-3">
        {isLoading && <div className="py-16 text-center text-sm text-waves-muted">Laden…</div>}
        {!isLoading && requests.length === 0 && (
          <EmptyState
            icon={<MapPin />}
            title="Geen open toegangsverzoeken"
            description="Zodra een hiring manager toegang vraagt tot een vestiging, verschijnt het verzoek hier."
            className="rounded-[16px] border border-waves-line"
          />
        )}
        {requests.map(req => (
          <div key={req.id} className="flex flex-wrap items-center gap-4 rounded-[16px] border border-waves-line bg-white px-5 py-4">
            <UserAvatar name={req.user_name} size={40} />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="text-[15px] font-bold">{req.user_name} vraagt toegang tot {req.branch_name}</div>
              <div className="text-[13px] text-waves-muted">
                {req.user_email} · <span className="font-mono">{new Date(req.created_at).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button variant="destructive" size="sm" onClick={() => denyMut.mutate(req.id)} disabled={busy}>Afwijzen</Button>
              <Button variant="ink" size="sm" onClick={() => grantMut.mutate(req.id)} disabled={busy}>Toegang geven</Button>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}
